'use client'

import React, { memo } from 'react'
import { Cpu } from 'lucide-react'

// Types simplifiés pour le composant - compatibles avec page.tsx
interface ProcessorElementProps {
  element: any // WorkspaceElement from page.tsx
  processor: any // LEDProcessor from page.tsx
  isSelected: boolean
  isPreviewSelected: boolean
  isDragging: boolean
  isSelectedForConnection: boolean
  currentStep: string
  showResizeHandle: boolean
  panelConnections: Array<{
    processorId: string
    panelId: string
    port: number
    order: number
  }>
  mireConnections?: Array<{
    imageId: string
    processorId: string
    port: number
    gridPosition: {
      row: number
      col: number
    }
    order: number
  }>
  backupPorts: Record<string, Record<number, number>>
  backupProcessors?: Record<string, string> // { [primaryProcessorId]: backupProcessorId }
  getBackupProcessor?: (processorId: string) => string | null
  getPrimaryProcessor?: (backupProcessorId: string) => string | null
  isProcessorBackup?: (processorId: string) => boolean
  getProcessorImageUrl: (processor: any) => string
  getActualImageBounds: (element: any) => { x: number; y: number; width: number; height: number }
  onClick: (element: any) => void
  onResizeStart?: (e: React.MouseEvent, element: any) => void
}

const ProcessorElement = memo(({
  element,
  processor,
  isSelected,
  isPreviewSelected,
  isDragging,
  isSelectedForConnection,
  currentStep,
  showResizeHandle,
  panelConnections,
  mireConnections = [],
  backupPorts,
  backupProcessors = {},
  getBackupProcessor,
  getPrimaryProcessor,
  isProcessorBackup,
  getProcessorImageUrl,
  getActualImageBounds,
  onClick,
  onResizeStart
}: ProcessorElementProps) => {
  const hasImage = !!processor.image
  
  // Vérifier si ce processeur est un backup ou a un backup
  const isBackup = isProcessorBackup ? isProcessorBackup(element.id) : false
  const backupProcessorId = getBackupProcessor ? getBackupProcessor(element.id) : null
  const primaryProcessorId = getPrimaryProcessor ? getPrimaryProcessor(element.id) : null
  const bounds = hasImage ? getActualImageBounds(element) : {
    x: element.position.x,
    y: element.position.y,
    width: element.size.width,
    height: element.size.height
  }

  // Nombre total de ports du processeur
  const totalPorts = typeof processor?.outputPorts === 'number' ? processor.outputPorts : 0

  // Calcul des connexions Main et Backup
  const processorPanelConnections = panelConnections.filter(conn => conn.processorId === element.id)
  const processorMireConnections = mireConnections.filter(conn => conn.processorId === element.id)

  // Combiner toutes les connexions (panneaux + mires)
  const allProcessorConnections = [
    ...processorPanelConnections.map(conn => ({ port: conn.port })),
    ...processorMireConnections.map(conn => ({ port: conn.port }))
  ]

  const processorBackupPorts = backupPorts[element.id] || {}
  const backupPortNumbers = Object.values(processorBackupPorts)

  const mainConnections = allProcessorConnections.filter(conn => !backupPortNumbers.includes(conn.port))
  const backupConnections = allProcessorConnections.filter(conn => backupPortNumbers.includes(conn.port))

  // Compter les ports Main uniques utilisés
  const mainPortsUsed = new Set(mainConnections.map(conn => conn.port)).size

  // Compter les Backups : uniquement ceux dont le port principal (main) est utilisé
  const usedMainPorts = new Set(mainConnections.map(conn => conn.port))
  const backupPortsUsed = Object.keys(processorBackupPorts).filter(mainPort =>
    usedMainPorts.has(Number(mainPort))
  ).length

  return (
    <g style={{ pointerEvents: hasImage ? 'none' : 'auto' } as any} data-onboarding="workspace-processor">
      {hasImage ? (
        <>
          {/* Processeur avec image */}
          {/* Indicateur de sélection pour connexion */}
          {isSelectedForConnection && currentStep === 'data' && (
            <>
              <rect
                x={bounds.x - 8}
                y={bounds.y - 8}
                width={bounds.width + 16}
                height={bounds.height + 16}
                fill="none"
                stroke="hsl(var(--primary))"
                strokeWidth="3"
                rx="12"
                opacity="0.3"
                pointerEvents="none"
              >
                <animate attributeName="opacity" values="0.3;0.6;0.3" dur="2s" repeatCount="indefinite" />
              </rect>
              <rect
                x={bounds.x - 4}
                y={bounds.y - 4}
                width={bounds.width + 8}
                height={bounds.height + 8}
                fill="none"
                stroke="hsl(var(--primary))"
                strokeWidth="2"
                rx="10"
                pointerEvents="none"
              >
                <animate attributeName="stroke-width" values="2;3;2" dur="1.5s" repeatCount="indefinite" />
              </rect>
            </>
          )}
          
          {/* Image du processeur */}
          <image
            x={element.position.x}
            y={element.position.y}
            width={element.size.width}
            height={element.size.height}
            href={getProcessorImageUrl(processor)}
            preserveAspectRatio="xMidYMid meet"
            className={`cursor-move ${isDragging ? 'opacity-80' : ''}`}
            onClick={() => onClick(element)}
            style={{
              imageRendering: 'auto',
              pointerEvents: 'auto'
            } as any}
          />
          
          {/* Bordure de sélection */}
          {(isSelected || isPreviewSelected) && (
            <rect
              x={bounds.x - 2}
              y={bounds.y - 2}
              width={bounds.width + 4}
              height={bounds.height + 4}
              fill="none"
              stroke="hsl(var(--primary))"
              strokeWidth={isSelected ? "3" : "2"}
              strokeDasharray={isPreviewSelected ? "4,2" : "none"}
              rx="10"
              pointerEvents="none"
            />
          )}
          
          {/* Informations de connexion sous le processeur */}
          <text
            x={bounds.x + bounds.width / 2}
            y={bounds.y + bounds.height + 16}
            textAnchor="middle"
            className="text-xs fill-foreground"
            style={{ fontSize: '11px', fontWeight: '600', pointerEvents: 'none' } as any}
          >
            {processor.customName || `${processor.brand} ${processor.model}`}
          </text>
          {/* Afficher Main ou Backup selon le statut du processeur */}
          {isBackup ? (
            <text
              x={bounds.x + bounds.width / 2}
              y={bounds.y + bounds.height + 30}
              textAnchor="middle"
              className="text-xs fill-destructive"
              style={{ fontSize: '10px', fontWeight: 'bold', pointerEvents: 'none' } as any}
            >
              Backup processor | {totalPorts} ports
            </text>
          ) : backupProcessorId ? (
            <text
              x={bounds.x + bounds.width / 2}
              y={bounds.y + bounds.height + 30}
              textAnchor="middle"
              className="text-xs fill-primary"
              style={{ fontSize: '10px', fontWeight: 'bold', pointerEvents: 'none' } as any}
            >
              Main processor | {totalPorts} ports
            </text>
          ) : (
            <text
              x={bounds.x + bounds.width / 2}
              y={bounds.y + bounds.height + 30}
              textAnchor="middle"
              className="text-xs fill-muted-foreground"
              style={{ fontSize: '10px', pointerEvents: 'none' } as any}
            >
              Main: {mainPortsUsed} | Backup: {backupPortsUsed}
            </text>
          )}
          
          {/* Badge Backup Processeur */}
          {isBackup && (
            <g>
              <rect
                x={bounds.x + bounds.width - 55}
                y={bounds.y - 28}
                width="50"
                height="24"
                fill="hsl(var(--destructive))"
                stroke="hsl(var(--background))"
                strokeWidth="2"
                rx="6"
                opacity="0.95"
              />
              <text
                x={bounds.x + bounds.width - 30}
                y={bounds.y - 13}
                textAnchor="middle"
                className="fill-white"
                style={{ fontSize: '12px', fontWeight: 'bold', pointerEvents: 'none' } as any}
              >
                BACKUP
              </text>
            </g>
          )}
          
          {/* Indicateur qu'un processeur a un backup */}
          {backupProcessorId && (
            <g>
              <circle
                cx={bounds.x + bounds.width - 10}
                cy={bounds.y + 10}
                r="6"
                fill="hsl(var(--primary))"
                stroke="hsl(var(--background))"
                strokeWidth="1.5"
                opacity="0.9"
              />
              <text
                x={bounds.x + bounds.width - 10}
                y={bounds.y + 13}
                textAnchor="middle"
                className="text-xs fill-white"
                style={{ fontSize: '8px', fontWeight: 'bold', pointerEvents: 'none' } as any}
              >
                B
              </text>
            </g>
          )}
        </>
      ) : (
        <>
          {/* Processeur traditionnel avec rectangle */}
          {isSelectedForConnection && currentStep === 'data' && (
            <>
              <rect
                x={element.position.x - 8}
                y={element.position.y - 8}
                width={element.size.width + 16}
                height={element.size.height + 16}
                fill="none"
                stroke="hsl(var(--primary))"
                strokeWidth="3"
                rx="12"
                opacity="0.3"
              >
                <animate attributeName="opacity" values="0.3;0.6;0.3" dur="2s" repeatCount="indefinite" />
              </rect>
              <rect
                x={element.position.x - 4}
                y={element.position.y - 4}
                width={element.size.width + 8}
                height={element.size.height + 8}
                fill="none"
                stroke="hsl(var(--primary))"
                strokeWidth="2"
                rx="10"
              >
                <animate attributeName="stroke-width" values="2;3;2" dur="1.5s" repeatCount="indefinite" />
              </rect>
            </>
          )}
          
          <rect
            x={element.position.x}
            y={element.position.y}
            width={element.size.width}
            height={element.size.height}
            fill={
              isSelected ? "hsl(var(--led-processor))" :
              isPreviewSelected ? "hsl(var(--primary) / 0.3)" :
              "hsl(var(--led-processor))"
            }
            stroke={
              isSelected ? "hsl(var(--primary))" :
              isPreviewSelected ? "hsl(var(--primary))" :
              "hsl(var(--border))"
            }
            strokeWidth={
              isSelected ? "3" :
              isPreviewSelected ? "2" :
              "2"
            }
            strokeDasharray={isPreviewSelected ? "4,2" : "none"}
            rx="8"
            className={`cursor-move pulse-glow ${isDragging ? 'opacity-80' : ''}`}
            onClick={() => onClick(element)}
          />
          
          <foreignObject
            x={element.position.x + 8}
            y={element.position.y + 8}
            width={element.size.width - 16}
            height={element.size.height - 16}
          >
            <div className="text-xs text-white p-1 flex flex-col h-full">
              <Cpu className="h-4 w-4 mb-1" />
              <span className="font-medium">{processor.brand}</span>
              <span className="text-xs opacity-80">{processor.model}</span>
            </div>
          </foreignObject>
          
          {/* Informations de connexion sous le processeur */}
          <text
            x={element.position.x + element.size.width / 2}
            y={element.position.y + element.size.height + 16}
            textAnchor="middle"
            className="text-xs fill-foreground"
            style={{ fontSize: '11px', fontWeight: '600', pointerEvents: 'none' } as any}
          >
            {processor.customName || `${processor.brand} ${processor.model}`}
          </text>
          {/* Afficher Main ou Backup selon le statut du processeur */}
          {isBackup ? (
            <text
              x={element.position.x + element.size.width / 2}
              y={element.position.y + element.size.height + 30}
              textAnchor="middle"
              className="text-xs fill-destructive"
              style={{ fontSize: '10px', fontWeight: 'bold', pointerEvents: 'none' } as any}
            >
              Backup processor | {totalPorts} ports
            </text>
          ) : backupProcessorId ? (
            <text
              x={element.position.x + element.size.width / 2}
              y={element.position.y + element.size.height + 30}
              textAnchor="middle"
              className="text-xs fill-primary"
              style={{ fontSize: '10px', fontWeight: 'bold', pointerEvents: 'none' } as any}
            >
              Main processor | {totalPorts} ports
            </text>
          ) : (
            <text
              x={element.position.x + element.size.width / 2}
              y={element.position.y + element.size.height + 30}
              textAnchor="middle"
              className="text-xs fill-muted-foreground"
              style={{ fontSize: '10px', pointerEvents: 'none' } as any}
            >
              Main: {mainPortsUsed} | Backup: {backupPortsUsed}
            </text>
          )}
          
          {/* Badge Backup Processeur */}
          {isBackup && (
            <g>
              <rect
                x={element.position.x + element.size.width - 55}
                y={element.position.y - 28}
                width="80"
                height="24"
                fill="hsl(var(--destructive))"
                stroke="hsl(var(--background))"
                strokeWidth="2"
                rx="6"
                opacity="0.95"
              />
              <text
                x={element.position.x + element.size.width - 30}
                y={element.position.y - 13}
                textAnchor="middle"
                className="fill-white"
                style={{ fontSize: '12px', fontWeight: 'bold', pointerEvents: 'none' } as any}
              >
                BACKUP
              </text>
            </g>
          )}
          
          {/* Indicateur qu'un processeur a un backup */}
          {backupProcessorId && (
            <g>
              <circle
                cx={element.position.x + element.size.width - 10}
                cy={element.position.y + 10}
                r="6"
                fill="hsl(var(--primary))"
                stroke="hsl(var(--background))"
                strokeWidth="1.5"
                opacity="0.9"
              />
              <text
                x={element.position.x + element.size.width - 10}
                y={element.position.y + 13}
                textAnchor="middle"
                className="text-xs fill-white"
                style={{ fontSize: '8px', fontWeight: 'bold', pointerEvents: 'none' } as any}
              >
                B
              </text>
            </g>
          )}
        </>
      )}
      
      {/* Handle de redimensionnement */}
      {showResizeHandle && onResizeStart && (
        <g
          data-resize-handle="true"
          className="cursor-nwse-resize"
          style={{ userSelect: 'none', pointerEvents: 'auto' } as any}
          onMouseDown={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onResizeStart(e, element)
          }}
        >
          <rect
            data-resize-handle="true"
            x={bounds.x + bounds.width - 7}
            y={bounds.y + bounds.height - 7}
            width="12"
            height="12"
            fill="hsl(var(--primary))"
            stroke="hsl(var(--background))"
            strokeWidth="1.5"
            rx="2"
          />
          <line
            x1={bounds.x + bounds.width - 3}
            y1={bounds.y + bounds.height + 2}
            x2={bounds.x + bounds.width + 2}
            y2={bounds.y + bounds.height - 3}
            stroke="hsl(var(--background))"
            strokeWidth="1"
          />
          <line
            x1={bounds.x + bounds.width - 3}
            y1={bounds.y + bounds.height - 1}
            x2={bounds.x + bounds.width - 1}
            y2={bounds.y + bounds.height - 3}
            stroke="hsl(var(--background))"
            strokeWidth="1"
          />
        </g>
      )}
    </g>
  )
}, (prevProps, nextProps) => {
  // Comparaison personnalisée pour éviter les re-renders inutiles
  
  // Vérifier les props de base
  if (
    prevProps.element.id !== nextProps.element.id ||
    prevProps.element.position.x !== nextProps.element.position.x ||
    prevProps.element.position.y !== nextProps.element.position.y ||
    prevProps.element.size.width !== nextProps.element.size.width ||
    prevProps.element.size.height !== nextProps.element.size.height ||
    prevProps.isSelected !== nextProps.isSelected ||
    prevProps.isPreviewSelected !== nextProps.isPreviewSelected ||
    prevProps.isDragging !== nextProps.isDragging ||
    prevProps.isSelectedForConnection !== nextProps.isSelectedForConnection ||
    prevProps.currentStep !== nextProps.currentStep ||
    prevProps.showResizeHandle !== nextProps.showResizeHandle ||
    prevProps.processor.image !== nextProps.processor.image ||
    prevProps.processor.customName !== nextProps.processor.customName
  ) {
    return false
  }
  
  // Comparer les connexions panneaux pour ce processeur uniquement
  const prevConnections = prevProps.panelConnections.filter(c => c.processorId === prevProps.element.id)
  const nextConnections = nextProps.panelConnections.filter(c => c.processorId === nextProps.element.id)

  if (prevConnections.length !== nextConnections.length) {
    return false
  }

  // Comparer les connexions mires pour ce processeur uniquement
  const prevMireConnections = (prevProps.mireConnections || []).filter(c => c.processorId === prevProps.element.id)
  const nextMireConnections = (nextProps.mireConnections || []).filter(c => c.processorId === nextProps.element.id)

  if (prevMireConnections.length !== nextMireConnections.length) {
    return false
  }

  // Comparer les ports backup pour ce processeur
  const prevBackup = prevProps.backupPorts[prevProps.element.id]
  const nextBackup = nextProps.backupPorts[nextProps.element.id]
  const prevBackupStr = JSON.stringify(prevBackup || {})
  const nextBackupStr = JSON.stringify(nextBackup || {})

  if (prevBackupStr !== nextBackupStr) {
    return false
  }

  // Comparer les processeurs backup
  const prevIsBackup = prevProps.isProcessorBackup ? prevProps.isProcessorBackup(prevProps.element.id) : false
  const nextIsBackup = nextProps.isProcessorBackup ? nextProps.isProcessorBackup(nextProps.element.id) : false
  if (prevIsBackup !== nextIsBackup) {
    return false
  }

  const prevBackupProc = prevProps.getBackupProcessor ? prevProps.getBackupProcessor(prevProps.element.id) : null
  const nextBackupProc = nextProps.getBackupProcessor ? nextProps.getBackupProcessor(nextProps.element.id) : null
  if (prevBackupProc !== nextBackupProc) {
    return false
  }

  return true
})

ProcessorElement.displayName = 'ProcessorElement'

export default ProcessorElement

